/**
 * Admin operations views: components, upgrades, backup, operator, settings.
 *
 * Both languages live side by side so a review sees the pair in one diff.
 * `index.ts` merges this module into the catalogs under the `adminOps` namespace.
 */
export const de = {
  components: {
    title: "Komponenten",
    subtitleCountSuffix: "Helm-Komponenten",
    subtitleEmpty: "Helm-Komponenten und Deployments",
    searchPlaceholder: "Suche nach Name, Chart, Mandant...",
    searchAriaLabel: "Komponenten durchsuchen",
    tenantFilterAriaLabel: "Nach Mandant filtern",
    allTenantsOption: "Alle Mandanten",
    table: {
      chart: "Chart",
      tenant: "Mandant",
      chartVersion: "Chart Version",
      installedVersion: "Installiert",
      phase: "Phase",
      active: "Aktiv",
      message: "Nachricht",
    },
    yes: "Ja",
    no: "Nein",
    rowDetailsAriaLabel: "Details anzeigen",
    empty: "Keine OpenDeskComponent CRs gefunden. Komponenten werden bei der Mandanten-Provisionierung erstellt.",
    tableDefaultEmpty: "Keine Daten vorhanden.",
  },
  upgrades: {
    title: "Upgrades",
    subtitle: "Releases und Upgrade-Pläne",
    releasesCardTitle: "Verfügbare Releases",
    releasesTable: {
      version: "Version",
      phase: "Phase",
      verified: "Verifiziert",
      charts: "Charts",
      images: "Images",
      date: "Datum",
    },
    emptyReleasesPrefix: "Keine OpenDeskRelease CRs gefunden. Generieren Sie ein Release-Manifest mit:",
    plansCardTitle: "Upgrade-Pläne",
    plansTable: {
      name: "Name",
      target: "Ziel",
      strategy: "Strategie",
      phase: "Phase",
      from: "Von",
      started: "Gestartet",
    },
    emptyPlans: "Keine aktiven Upgrade-Pläne. Starten Sie ein Upgrade über die Mandanten-Aktionen.",
  },
  backup: {
    title: "Backup & DR",
    subtitle: "Sicherungen und Wiederherstellung",
    policiesCardTitle: "Backup-Policies",
    policiesTable: {
      tenant: "Mandant",
      schedule: "Zeitplan (UTC)",
      type: "Typ",
      retention: "Aufbewahrung",
      phase: "Phase",
      successFailure: "Erfolg/Fehler",
      lastBackup: "Letztes Backup",
      nextBackup: "Nächstes",
    },
    retentionDaysSuffix: "Tage",
    emptyPolicies: "Keine Backup-Policies gefunden. Policies werden pro Mandant konfiguriert.",
    backupsCardTitle: "Letzte Backups",
    backupsTable: {
      name: "Name",
      type: "Typ",
      phase: "Phase",
      size: "Größe",
      time: "Zeitpunkt",
    },
    emptyBackups: "Keine Backups vorhanden. Starten Sie ein Backup über die Mandanten-Aktionen.",
  },
  operator: {
    title: "Operator",
    noClusterPrefix: "Kein OpenDeskCluster CR gefunden. Erstellen Sie einen mit:",
    kpi: {
      version: "Version",
      complianceScore: "Compliance Score",
      unhealthyPods: "Unhealthy Pods",
    },
    configCardTitle: "Konfiguration",
    config: {
      domain: "Cluster-Domain (Standard)",
      releaseChannel: "Release Channel",
      complianceProfile: "Compliance Profile",
      workloadHealth: "Workload Health",
      lastReconcile: "Letzte Reconciliation",
    },
    driftTitle: "Drift erkannt",
    componentsCardTitle: "Komponenten",
    componentsTable: {
      name: "Name",
      chartVersion: "Chart Version",
      appVersion: "App Version",
      status: "Status",
      message: "Nachricht",
    },
    noComponents: "Keine Komponenten registriert.",
  },
  settings: {
    title: "Einstellungen",
    subtitle: "Cluster-Konfiguration, Feature Gates und Abhängigkeiten",
    clusterConfigCardTitle: "Cluster-Konfiguration",
    noCluster: "Kein OpenDeskCluster CR gefunden. Konfigurationsdaten werden nach Erstellung angezeigt.",
    clusterConfig: {
      name: "Name",
      domain: "Cluster-Domain (Standard)",
      version: "Version",
      installedVersion: "Installierte Version",
      edition: "Edition",
      releaseChannel: "Release Channel",
      complianceProfile: "Compliance Profile",
    },
    featureGatesCardTitle: "Feature Gates",
    featureGatesDesc: "Plattform-weite Schalter für optionale und experimentelle Funktionen. Werte werden aus OpenDeskCluster.spec.featureGates gelesen; Standardwerte gelten, wenn nichts überschrieben wurde.",
    featureGatesTable: {
      name: "Name",
      description: "Beschreibung",
      default: "Standard",
      status: "Status",
    },
    active: "aktiv",
    inactive: "inaktiv",
    noFeatureGates: "Keine Feature Gates verfügbar.",
    dependenciesCardTitle: "Abhängigkeiten",
    dependenciesDesc: "Externe Dienste, die der Operator erkennt und verwendet (PostgreSQL, Redis, Velero, …). Die Richtlinien werden aus OpenDeskCluster.spec.dependencies gelesen; der Status wird bei jeder Reconciliation aktualisiert. Fehlende Abhängigkeiten werden nicht automatisch bereitgestellt (das übernimmt ArgoCD).",
    dependenciesTable: {
      name: "Name",
      policy: "Richtlinie",
      status: "Status",
      message: "Nachricht",
    },
    fulfilled: "erfüllt",
    open: "offen",
    noDependencies: "Keine Abhängigkeiten deklariert.",
    readOnlyNotice: "Nur der Enterprise-Schalter und die Registry-Mirrors sind hier änderbar, alles andere im OpenDeskCluster CR.",
    enterpriseSwitch: "Enterprise Edition aktivieren",
    gateSaveFailed: "Schalter konnte nicht gespeichert werden.",
    mirrors: {
      title: "Registry-Mirrors",
      info: "Ein Mirror ersetzt ein Registry-Präfix für alle Image- und Chart-Referenzen. Mit Zugangsdaten wird das Secret aus dem Operator-Namespace als Pull-Secret in die Tenant-Namespaces kopiert. Nur Enterprise beschränkt es auf Enterprise-Tenants. Chart-Pulls nutzen weiterhin zendis-enterprise-pull und registry-opencode.",
      infoLabel: "Hinweis zu Registry-Mirrors",
      empty: "Keine Mirrors konfiguriert.",
      source: "Quelle",
      mirror: "Mirror",
      credential: "Secret mit Zugangsdaten",
      enterpriseOnly: "Nur Enterprise",
      remove: "Mirror entfernen",
      add: "Mirror hinzufügen",
      save: "Mirrors speichern",
      saved: "Mirrors gespeichert.",
      saveFailed: "Mirrors konnten nicht gespeichert werden.",
      conflict: "Die Mirrors wurden zwischenzeitlich geändert. Seite neu laden und erneut speichern.",
      loadFailed: "Mirrors konnten nicht geladen werden.",
      entry: "Mirror",
    },
  },
} as const;

export const en = {
  components: {
    title: "Components",
    subtitleCountSuffix: "Helm components",
    subtitleEmpty: "Helm components and deployments",
    searchPlaceholder: "Search by name, chart, tenant...",
    searchAriaLabel: "Search components",
    tenantFilterAriaLabel: "Filter by tenant",
    allTenantsOption: "All tenants",
    table: {
      chart: "Chart",
      tenant: "Tenant",
      chartVersion: "Chart version",
      installedVersion: "Installed",
      phase: "Phase",
      active: "Active",
      message: "Message",
    },
    yes: "Yes",
    no: "No",
    rowDetailsAriaLabel: "Show details",
    empty: "No OpenDeskComponent CRs found. Components are created during tenant provisioning.",
    tableDefaultEmpty: "No data available.",
  },
  upgrades: {
    title: "Upgrades",
    subtitle: "Releases and upgrade plans",
    releasesCardTitle: "Available releases",
    releasesTable: {
      version: "Version",
      phase: "Phase",
      verified: "Verified",
      charts: "Charts",
      images: "Images",
      date: "Date",
    },
    emptyReleasesPrefix: "No OpenDeskRelease CRs found. Generate a release manifest with:",
    plansCardTitle: "Upgrade plans",
    plansTable: {
      name: "Name",
      target: "Target",
      strategy: "Strategy",
      phase: "Phase",
      from: "From",
      started: "Started",
    },
    emptyPlans: "No active upgrade plans. Start an upgrade from the tenant actions.",
  },
  backup: {
    title: "Backup & DR",
    subtitle: "Backups and recovery",
    policiesCardTitle: "Backup policies",
    policiesTable: {
      tenant: "Tenant",
      schedule: "Schedule (UTC)",
      type: "Type",
      retention: "Retention",
      phase: "Phase",
      successFailure: "Success/failure",
      lastBackup: "Last backup",
      nextBackup: "Next",
    },
    retentionDaysSuffix: "days",
    emptyPolicies: "No backup policies found. Policies are configured per tenant.",
    backupsCardTitle: "Recent backups",
    backupsTable: {
      name: "Name",
      type: "Type",
      phase: "Phase",
      size: "Size",
      time: "Time",
    },
    emptyBackups: "No backups yet. Start a backup from the tenant actions.",
  },
  operator: {
    title: "Operator",
    noClusterPrefix: "No OpenDeskCluster CR found. Create one with:",
    kpi: {
      version: "Version",
      complianceScore: "Compliance score",
      unhealthyPods: "Unhealthy pods",
    },
    configCardTitle: "Configuration",
    config: {
      domain: "Cluster domain (default)",
      releaseChannel: "Release channel",
      complianceProfile: "Compliance profile",
      workloadHealth: "Workload health",
      lastReconcile: "Last reconciliation",
    },
    driftTitle: "Drift detected",
    componentsCardTitle: "Components",
    componentsTable: {
      name: "Name",
      chartVersion: "Chart version",
      appVersion: "App version",
      status: "Status",
      message: "Message",
    },
    noComponents: "No components registered.",
  },
  settings: {
    title: "Settings",
    subtitle: "Cluster configuration, feature gates and dependencies",
    clusterConfigCardTitle: "Cluster configuration",
    noCluster: "No OpenDeskCluster CR found. Configuration data appears once one is created.",
    clusterConfig: {
      name: "Name",
      domain: "Cluster domain (default)",
      version: "Version",
      installedVersion: "Installed version",
      edition: "Edition",
      releaseChannel: "Release channel",
      complianceProfile: "Compliance profile",
    },
    featureGatesCardTitle: "Feature gates",
    featureGatesDesc: "Platform-wide switches for optional and experimental features. Values are read from OpenDeskCluster.spec.featureGates; defaults apply where nothing was overridden.",
    featureGatesTable: {
      name: "Name",
      description: "Description",
      default: "Default",
      status: "Status",
    },
    active: "active",
    inactive: "inactive",
    noFeatureGates: "No feature gates available.",
    dependenciesCardTitle: "Dependencies",
    dependenciesDesc: "External services the operator detects and uses (PostgreSQL, Redis, Velero, …). Policies are read from OpenDeskCluster.spec.dependencies; status refreshes on every reconciliation. Missing dependencies are not provisioned automatically (ArgoCD handles that).",
    dependenciesTable: {
      name: "Name",
      policy: "Policy",
      status: "Status",
      message: "Message",
    },
    fulfilled: "fulfilled",
    open: "open",
    noDependencies: "No dependencies declared.",
    readOnlyNotice: "Only the enterprise switch and the registry mirrors are editable here, edit everything else in the OpenDeskCluster CR.",
    enterpriseSwitch: "Enable enterprise edition",
    gateSaveFailed: "Could not save the switch.",
    mirrors: {
      title: "Registry mirrors",
      info: "A mirror replaces a registry prefix in every image and chart reference. With a credential, the Secret from the operator namespace is copied into the tenant namespaces as a pull secret. Enterprise only limits it to enterprise tenants. Chart pulls still use zendis-enterprise-pull and registry-opencode.",
      infoLabel: "About registry mirrors",
      empty: "No mirrors configured.",
      source: "Source",
      mirror: "Mirror",
      credential: "Credential Secret",
      enterpriseOnly: "Enterprise only",
      remove: "Remove mirror",
      add: "Add mirror",
      save: "Save mirrors",
      saved: "Mirrors saved.",
      saveFailed: "Could not save the mirrors.",
      conflict: "The mirrors changed in the meantime. Reload the page and save again.",
      loadFailed: "Could not load the mirrors.",
      entry: "Mirror",
    },
  },
} as const;
